import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { copyFaqApi } from '../api';
import type { CopyFaqBundle, CopyFaqPayload } from '../types';
import { getDefaultBundle, getErrorMessage } from '../utils';

export function useCopyFaq() {
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<CopyFaqBundle>(getDefaultBundle());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const result = await copyFaqApi.getBundle(controller.signal);

      if (!mountedRef.current) return;

      setData(result);
    } catch (err) {
      if (mountedRef.current) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const replaceBundle = useCallback(async (payload: CopyFaqPayload) => {
    setSaving(true);

    try {
      return await copyFaqApi.replaceBundle(payload);
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    fetchData();

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, [fetchData]);

  return useMemo(
    () => ({
      ...data,
      loading,
      saving,
      error,
      refetch: fetchData,
      replaceBundle,
    }),
    [data, error, fetchData, loading, replaceBundle, saving]
  );
}
